import { useCallback, useEffect, useRef, useState } from 'react';

import { UNREGISTERED_POPUP_DELAY_MS, UNREGISTERED_POPUP_OPEN_LOCK_KEY, UNREGISTERED_POPUP_PRESENTED_KEY, UNREGISTERED_POPUP_STORAGE_KEY } from '@/shared-components/popups/unregistered-user-popup/constants';

import useStateHandler from "./useStateHandler";

let hasGlobalMandatoryAuthReminderTriggered = false;

function acquireMandatoryAuthReminder() {
  if (hasGlobalMandatoryAuthReminderTriggered) {
    return false;
  }
  hasGlobalMandatoryAuthReminderTriggered = true;
  return true;
}

function releaseMandatoryAuthReminder() {
  hasGlobalMandatoryAuthReminderTriggered = false;
}

export default function useProtectActionUnregistered(protectOptions) {
  const { state, stateHandler } = useStateHandler();
  const [showPopup, setShowPopup] = useState(false);
  const typeOfProtection = protectOptions?.typeOfProtection;
  const autoOpen = protectOptions?.autoOpen !== false;
  const isEnabled = protectOptions?.isEnabled ?? true;
  const rawPopupDelayMs = protectOptions?.popupDelayMs;
  const popupDelayMs = Number.isFinite(rawPopupDelayMs)
    ? Math.max(0, rawPopupDelayMs)
    : UNREGISTERED_POPUP_DELAY_MS;
  const isBrowser = typeof window !== 'undefined';
  const hasLockRef = useRef(false);
  const autoAuthTriggeredRef = useRef(false);
  const resetAutoAuthFlags = useCallback(() => {
    autoAuthTriggeredRef.current = false;
    releaseMandatoryAuthReminder();
  }, []);

  const acquireLock = useCallback(() => {
    if (!isBrowser) {
      return false;
    }

    if (hasLockRef.current) {
      return true;
    }

    if (localStorage.getItem(UNREGISTERED_POPUP_OPEN_LOCK_KEY) === 'true') {
      return false;
    }

    localStorage.setItem(UNREGISTERED_POPUP_OPEN_LOCK_KEY, 'true');
    hasLockRef.current = true;
    return true;
  }, [isBrowser]);

  const releaseLock = useCallback(() => {
    if (!isBrowser) {
      hasLockRef.current = false;
      return;
    }

    if (hasLockRef.current) {
      localStorage.removeItem(UNREGISTERED_POPUP_OPEN_LOCK_KEY);
      hasLockRef.current = false;
    }
  }, [isBrowser]);

  useEffect(() => {
    const hasCompletedMandatory = isBrowser && localStorage.getItem(UNREGISTERED_POPUP_PRESENTED_KEY) === 'true';

    if (!isEnabled) {
      setShowPopup(false);
      releaseLock();
      resetAutoAuthFlags();
      return;
    }

    if (state.user_session) {
      setShowPopup(false);
      if (isBrowser) {
        localStorage.removeItem(UNREGISTERED_POPUP_STORAGE_KEY);
        localStorage.removeItem(UNREGISTERED_POPUP_PRESENTED_KEY);
      }
      resetAutoAuthFlags();
      releaseLock();
      return;
    }

    if (!isBrowser || typeOfProtection !== "USER_LIMIT_VIEW") {
      resetAutoAuthFlags();
      return;
    }

    if (hasCompletedMandatory) {
      if (showPopup) {
        return;
      }
      setShowPopup(false);
      releaseLock();
      if (isBrowser) {
        localStorage.removeItem(UNREGISTERED_POPUP_STORAGE_KEY);
      }
      if (!autoAuthTriggeredRef.current && acquireMandatoryAuthReminder()) {
        autoAuthTriggeredRef.current = true;
        stateHandler.set('unregisteredUser', state.unregisteredUser || 'guest_rest');
        stateHandler.set('action_denied', 'USER_ACCOUNT_REQUIRED');
      }
      return;
    }

    if (!autoOpen) {
      return;
    }

    const storedValue = localStorage.getItem(UNREGISTERED_POPUP_STORAGE_KEY);

    if (storedValue === 'true') {
      const hasLock = acquireLock();
      setShowPopup(hasLock);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      localStorage.setItem(UNREGISTERED_POPUP_STORAGE_KEY, 'true');
      if (acquireLock()) {
        setShowPopup(true);
      }
    }, popupDelayMs);

    return () => window.clearTimeout(timeoutId);
  }, [acquireLock, autoOpen, isBrowser, isEnabled, popupDelayMs, releaseLock, resetAutoAuthFlags, showPopup, state.user_session, state.unregisteredUser, stateHandler, typeOfProtection]);

  useEffect(() => () => releaseLock(), [releaseLock]);

  const privateActionHandlerUnregistered = useCallback((event, _id) => {
    if (!isBrowser || !isEnabled) {
      return;
    }

    if (_id) {
      localStorage.removeItem(UNREGISTERED_POPUP_STORAGE_KEY);
      setShowPopup(false);
      releaseLock();
      return;
    }

    if (typeOfProtection !== "USER_LIMIT_VIEW" || state.user_session) {
      return;
    }

    const hasCompletedMandatory = localStorage.getItem(UNREGISTERED_POPUP_PRESENTED_KEY) === 'true';

    if (hasCompletedMandatory) {
      setShowPopup(false);
      releaseLock();
      localStorage.removeItem(UNREGISTERED_POPUP_STORAGE_KEY);
      if (!autoAuthTriggeredRef.current && acquireMandatoryAuthReminder()) {
        autoAuthTriggeredRef.current = true;
        stateHandler.set('unregisteredUser', state.unregisteredUser || 'guest_rest');
        stateHandler.set('action_denied', 'USER_ACCOUNT_REQUIRED');
      }
      if (event?.type === "click" && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      return;
    }

    const popupReady = localStorage.getItem(UNREGISTERED_POPUP_STORAGE_KEY) === 'true';

    if (popupReady && acquireLock()) {
      setShowPopup(true);
      if (event?.type === "click" && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
    }
  }, [acquireLock, isBrowser, isEnabled, releaseLock, state.user_session, state.unregisteredUser, stateHandler, typeOfProtection]);

  return { privateActionHandlerUnregistered, showPopup };
}
